// Client Internal Audit — AUDITOR portal page (/jobs/:id/ia; reached from My Audits → Open audit). Same 3-step
// pattern as the bizSAFE Audit Execution page:
//  1. Start — audit start time ("Now") + session photo (or a reason if none; kept on record, not in the report)
//     + how the audit is recorded (fill in on AuditPM / upload the filled checklist).
//  2. Checklist — the CCIS checklist graded Conform / Obs / NC / N/A with notes + close-out (whom / date) (last audit's
//     result + note alongside, "Copy last note"), or the uploaded checklist PDF + typed counts.
//  3. Report — Management Representative, who to send to, which reports, preview (draft), send. Paid → sent to the
//     client now; unpaid → Accounts told, sent once paid. Returned → "Send updated reports". Awaiting close-out →
//     close findings → "Send Close-out Report".
// Everything saves as you go ("✓ Synced" / "Save now", as bizSAFE).
//
// TWO AUDITORS (Oct 2026): each auditor works on their own device. Only what you change is saved (lib/iaSaveQueue —
// never the whole page), unsaved changes are kept on the device and retried on bad Wi-Fi, and the co-auditor's work
// appears every 20 seconds and whenever you come back to the page. Before signing or sending, the page checks the
// latest saved copy. Once the second auditor signs, the report is locked for both (the database enforces it); the
// lead can send it or "Unlock to edit" (which removes the second signature). Signatures are written by the server.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { oneLine } from '../lib/oneLine'
import { onTabReturn } from '../lib/onTabReturn'
import { createSaveQueue } from '../lib/iaSaveQueue'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { toast, failed } from '../lib/toast'
import { standardsLabel } from '../lib/service'
import IAReportDocument from '../components/IAReportDocument'
import TakePhotoButton from '../components/TakePhotoButton'
import { iaContactOf } from '../components/IAContactLine'
import { resolveMyAuditor, slotForAuditor } from '../lib/auditorAssignment'
import { RESULTS, isFinding, answerable, clauseOf, countFindings, conclusionText, pickChecklists, loadItems,
  buildSnapshot, iaAction, longDate, KIND_LABEL } from '../lib/iaReport'

const norm = s => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim()
const RESULT_COLOR = { Conform: '#27500a', Obs: '#8a5a00', NC: '#a32020', 'N/A': '#666' }
const STEPS = ['Start', 'Checklist', 'Report']
// ISO timestamp ⇄ <input type="datetime-local"> (local time), and the report's "Audit Start Time" (e.g. 0915, SGT).
const toLocalInput = iso => { if (!iso) return ''; const d = new Date(iso); const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}` }
const startTimeText = iso => iso ? new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit' }).replace(':', '') : ''
const sgDay = iso => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' })   // YYYY-MM-DD
const POLL_MS = 20000
// The report settings as the page uses them (defaults filled in). The SAME rule on every device, so both auditors
// see the same values for anything not saved yet.
function normRep(r0, ids, contactName) {
  return {
    ...r0, checklist_ids: ids,
    mode: r0.mode || ((ids || []).length ? null : 'upload'),
    mgmt_rep: r0.mgmt_rep ?? (contactName || ''),
    // Upload mode: typed NC / Obs counts (an older Major / Minor entry is added into NC).
    counts: r0.counts ? { nc: (+r0.counts.nc || 0) + (+r0.counts.major || 0) + (+r0.counts.minor || 0), obs: +r0.counts.obs || 0 } : { nc: 0, obs: 0 },
    clean: !!r0.clean, closeout: !!r0.closeout,
  }
}
const byItem = rows => Object.fromEntries((rows || []).map(r => [r.item_id, r]))
// Shrink a phone photo before upload (faster on site Wi-Fi). Anything the browser can't read (e.g. an iPhone HEIC on
// Windows) is uploaded as it is.
async function shrinkPhoto(file) {
  try {
    const url = URL.createObjectURL(file)
    const img = await new Promise((ok, bad) => { const i = new Image(); i.onload = () => ok(i); i.onerror = bad; i.src = url })
    URL.revokeObjectURL(url)
    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight))
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file
    const c = document.createElement('canvas')
    c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale)
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height)
    const blob = await new Promise(ok => c.toBlob(ok, 'image/jpeg', 0.85))
    return blob ? new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file
  } catch (e) { return file }
}

export default function IAWorksheet() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, user, isAuditor } = useAuth()
  const [job, setJob] = useState(null)
  const [cycle, setCycle] = useState(null)
  const [lists, setLists] = useState([])            // [{ id, title, standards, items }]
  const [missing, setMissing] = useState([])        // standards with no checklist
  const [resp, setResp] = useState({})              // item_id → response
  const [hist, setHist] = useState({ byId: {}, byText: {}, date: null })
  const [files, setFiles] = useState([])
  const [rep, setRep] = useState({})                // jobs.ia_report working copy
  const [filter, setFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [to, setTo] = useState('')
  const [step, setStep] = useState(1)
  const [saveState, setSaveState] = useState('saved')   // saved | saving | pending (as bizSAFE: "✓ Synced")
  const [pendingCount, setPendingCount] = useState(0)    // changes on this device not saved yet
  const [changedRows, setChangedRows] = useState(() => new Set())   // rows the co-auditor changed (shown before send)
  const [resetAt, setResetAt] = useState({})            // itemId → { field, t }: a typed box that lost a clash shows theirs
  const [linked, setLinked] = useState(true)            // my login is linked to my auditor record (server can see me)
  const queueRef = useRef(null)
  const flushTimer = useRef(null)
  const refreshing = useRef(false)
  const whoRef = useRef('Auditor')
  const serverResp = useRef({})                         // answers as last SAVED (not this device's queued changes)
  const contactRef = useRef('')                         // client contact = default Management Representative
  const keepVisible = useRef(new Set())                 // "Not answered" filter: rows answered just now stay visible
  const checklistFrozen = useRef(false)
  const [mySlot, setMySlot] = useState(null)        // 'auditor1' | 'auditor2' when I am an assigned auditor
  const [preview, setPreview] = useState(null)      // { snaps: { kind: snapshot }, kind }
  const [logos, setLogos] = useState({})
  const [library, setLibrary] = useState([])        // all checklists (to spot a standards change after starting)

  useEffect(() => { if (profile?.id) load() }, [id, profile?.id])   // my slot needs my profile (as bizSAFE)

  // Keep in step with the co-auditor: every 20 s while the page is visible, when you come back to it, and when the
  // connection returns (unsaved changes are saved first). Queued changes are retried every 15 s.
  useEffect(() => {
    if (loading || !job) return undefined
    const tick = () => { if (document.visibilityState === 'visible') refresh() }
    const poll = setInterval(tick, POLL_MS)
    const retry = setInterval(() => { if (queueRef.current?.size()) flushQueue() }, 15000)
    const back = async () => { await flushQueue(); refresh() }
    const offTab = onTabReturn(back)
    window.addEventListener('online', back)
    return () => { clearInterval(poll); clearInterval(retry); offTab(); window.removeEventListener('online', back) }
  }, [loading, !!job, id])

  function makeQueue() {
    return createSaveQueue({
      storageKey: `ia-q:${id}:${profile?.id || 'me'}`,
      saveRep: async patch => { const { error } = await supabase.rpc('ia_report_patch', { p_job: id, p_patch: patch }); if (error) throw error },
      // Saved only where nobody changed the box since this page saw it (IA_RESPONSE_PATCH.sql).
      saveResp: async (itemId, cols, bases) => {
        const { data, error } = await supabase.rpc('ia_response_patch', { p_job: id, p_item: itemId, p_patch: cols, p_base: bases || {}, p_by: whoRef.current })
        if (error) throw error
        const conflicts = data?.conflicts || {}
        const row = serverResp.current[itemId] = { ...(serverResp.current[itemId] || {}) }
        Object.entries(cols).forEach(([k, v]) => { row[k] = k in conflicts ? conflicts[k]?.value ?? null : v })
        return { conflicts }
      },
      onChange: n => setPendingCount(n),
      // Refused by the database (e.g. the report was locked meanwhile) — tell the auditor and show what is saved.
      onRejected: err => { failed('Not saved', err?.message || 'Please reload the page'); refresh() },
      // The co-auditor changed this box first: theirs is kept and shown; the auditor sees what they had typed.
      onConflict: (itemId, field, mine, theirs) => {
        setResp(r => ({ ...r, [itemId]: { ...(r[itemId] || {}), [field]: theirs?.value ?? null, updated_by: theirs?.by || r[itemId]?.updated_by } }))
        setChangedRows(s => new Set(s).add(itemId))
        setResetAt(m => ({ ...m, [itemId]: { field, t: Date.now() } }))
        const yours = String(mine ?? '').trim()
        toast(`Not saved — ${theirs?.by || 'your co-auditor'} changed it first`, 'warning', {
          detail: `Their version is kept (row highlighted). Check it and re-enter yours if still needed.${yours ? ` Yours was: "${yours.length > 140 ? yours.slice(0, 140) + '…' : yours}"` : ''}`,
        })
      },
    })
  }
  async function flushQueue() {
    const q = queueRef.current
    if (!q || !q.size()) { setSaveState('saved'); return true }
    // After a laptop slept offline the login may need renewing first — never send a save without a login (it would
    // be refused); the changes simply stay queued until the login is back.
    try { const { data } = await supabase.auth.getSession(); if (!data?.session) { setSaveState('pending'); return false } }
    catch (e) { setSaveState('pending'); return false }
    setSaveState('saving')
    const ok = await q.flush()
    setSaveState(q.size() ? 'pending' : 'saved')
    return ok && !q.size()
  }
  function scheduleFlush() {
    setSaveState('saving')
    clearTimeout(flushTimer.current)
    flushTimer.current = setTimeout(flushQueue, 600)
  }
  // What is saved right now: the job's report fields, every answer, every file.
  async function fetchServer() {
    const [{ data: j, error }, { data: rs }, { data: ev }] = await Promise.all([
      supabase.from('jobs').select('ia_report, report_signatures, ia_stage, ia_report_sent_at, ia_closeout_sent_at, ia_return_reason, status, auditor_name, auditor2_name').eq('id', id).maybeSingle(),
      supabase.from('ia_responses').select('*').eq('job_id', id),
      supabase.from('audit_evidence_files').select('*').eq('job_id', id).is('archived_at', null).in('evidence_type', ['ia_checklist', 'ia_other', 'session_photo']).order('created_at'),
    ])
    return { j: error ? null : j, rs: rs || [], ev: ev || [] }
  }
  // Show the saved copy, with this device's unsaved changes on top (typing in progress is kept by the row itself).
  function applyServer({ j, rs, ev }) {
    const q = queueRef.current
    // The co-auditor pressed "Switch checklist": reload so both work on the same checklist (never answer hidden items).
    const savedIds = (j.ia_report || {}).checklist_ids
    const shownIds = lists.map(l => l.id)
    if (checklistFrozen.current && shownIds.length && String(savedIds || '') !== String(shownIds)) {
      toast('The checklist was changed', 'info', { detail: 'Your co-auditor switched the checklist — showing the new one.' })
      checklistFrozen.current = !!savedIds
      setLoading(true); load()
      return
    }
    setJob(prev => ({ ...prev, ...j }))
    serverResp.current = byItem(rs)
    setRep(prev => q.overlayRep(normRep(j.ia_report || {}, (j.ia_report || {}).checklist_ids || prev.checklist_ids, contactRef.current)))
    setResp(q.overlayResp(byItem(rs)))
    setFiles(ev)
    if (j.ia_report?.checklist_ids) checklistFrozen.current = true
  }
  // Background refresh. Skipped while this device still has changes going out, and thrown away if a save landed
  // while it was fetching — an older copy must never replace something just saved (or being typed).
  async function refresh() {
    const q = queueRef.current
    if (refreshing.current || !q || q.busy() || (q.size() && !q.lockedHeld())) return
    refreshing.current = true
    try {
      const before = q.saves()
      const s = await fetchServer()
      if (s.j && q.saves() === before && !q.size() && !q.busy()) applyServer(s)
    } catch (e) { /* offline — try again next time */ }
    refreshing.current = false
  }
  // The auditor's escape hatch: throw away what this device couldn't save, and show what is saved.
  async function discardUnsaved() {
    if (!window.confirm(`Discard ${pendingCount} change${pendingCount === 1 ? '' : 's'} that could not be saved on this device?\n\nWhat is already saved is not affected.`)) return
    queueRef.current?.clear()
    setSaveState('saved')
    const s = await fetchServer().catch(() => null)
    if (s?.j) applyServer(s)
  }

  async function load() {
    const { data: j } = await supabase.from('jobs').select('*, clients(*)').eq('id', id).maybeSingle()
    if (!j) { setLoading(false); return }
    const { data: cy } = j.ia_cycle_id ? await supabase.from('client_ia_cycles').select('*').eq('id', j.ia_cycle_id).maybeSingle() : { data: null }
    const { data: cls } = await supabase.from('ia_checklists').select('*')
    const r0 = j.ia_report || {}
    let ids = r0.checklist_ids
    let miss = []
    if (!ids) {
      const pick = pickChecklists(cy?.standards || [], cls || [])
      ids = pick.lists.map(c => c.id); miss = pick.missing
    } else {
      const have = new Set((cls || []).filter(c => ids.includes(c.id)).flatMap(c => c.standards || []))
      miss = (cy?.standards || []).filter(s => !have.has(s))
    }
    const allItems = await loadItems(ids)
    const ls = ids.map(cid => { const c = (cls || []).find(x => x.id === cid) || {}; return { id: cid, title: c.title, standards: c.standards || [], items: allItems.filter(it => it.checklist_id === cid) } })
    const [{ data: rs }, { data: ev }] = await Promise.all([
      supabase.from('ia_responses').select('*').eq('job_id', id),
      supabase.from('audit_evidence_files').select('*').eq('job_id', id).is('archived_at', null).in('evidence_type', ['ia_checklist', 'ia_other', 'session_photo']).order('created_at'),
    ])
    // Last audit of the same cycle (history side by side).
    let h = { byId: {}, byText: {}, date: null }
    if (j.ia_cycle_id) {
      const { data: prev } = await supabase.from('jobs').select('id, audit_date').eq('ia_cycle_id', j.ia_cycle_id).neq('id', id)
        .not('ia_report_sent_at', 'is', null).order('audit_date', { ascending: false }).limit(1)
      if (prev?.[0]) {
        const { data: pr } = await supabase.from('ia_responses').select('item_id, result, notes, ia_checklist_items(label, body)').eq('job_id', prev[0].id)
        h.date = prev[0].audit_date
        for (const x of pr || []) {
          h.byId[x.item_id] = x
          const k = norm(`${x.ia_checklist_items?.label || ''} ${x.ia_checklist_items?.body || ''}`)
          if (k && !h.byText[k]) h.byText[k] = x
        }
      }
    }
    const [{ data: auds }, { data: fs }] = await Promise.all([
      supabase.from('auditors').select('id, name, email, active, user_id'),
      supabase.from('firm_settings').select('firm_logo_base64, stamp_base64').eq('id', 1).maybeSingle(),
    ])
    // Which auditor am I / which slot — the same shared rule as bizSAFE (login link, then login email, then name).
    const me = resolveMyAuditor(auds || [], profile, user?.email)
    const slot = slotForAuditor(j, me, profile)
    setMySlot(slot)
    // The server and the database only recognise an auditor by login link or login email (not by name). If I'm only
    // matched by name, nothing I do would save — say so up front instead of failing on the day.
    const mail = String(user?.email || '').trim().toLowerCase()
    setLinked(!slot || !!(me && ((me.user_id && me.user_id === profile?.id) || (mail && String(me.email || '').trim().toLowerCase() === mail))))
    whoRef.current = profile?.name || user?.email || 'Auditor'
    contactRef.current = iaContactOf(j.clients).name || ''
    // One queue per job and login (opening another job from this page starts that job's own queue).
    const qKey = `${id}:${profile?.id}`
    if (!queueRef.current || queueRef.current.key !== qKey) queueRef.current = Object.assign(makeQueue(), { key: qKey })
    const q = queueRef.current
    setLogos({ company: fs?.firm_logo_base64 || '', stamp: fs?.stamp_base64 || '' })
    setJob(j); setCycle(cy); setLists(ls); setMissing(miss); setHist(h); setFiles(ev || []); setLibrary(cls || [])
    serverResp.current = byItem(rs)
    setResp(q.overlayResp(byItem(rs)))   // this device's unsaved changes (if any) stay on top
    setRep(q.overlayRep(normRep(r0, ids, contactRef.current)))
    // Once sent, the address the report actually went to comes first (the office may have changed it at send).
    setTo((j.ia_report_sent_at ? r0.last_to || r0.send_to : r0.send_to || r0.pending?.to || r0.last_to) || iaContactOf(j.clients).email || '')
    if (q.size()) flushQueue()
    // Open on the step that needs attention: the report once it has been sent / is waiting for close-out.
    setStep(j.ia_stage && j.ia_stage !== 'returned' ? 3 : 1)
    // The checklist choice follows the cycle's standards until the auditor starts recording (first answer / setting
    // saved) — from then on it is frozen in ia_report.checklist_ids so a later change can't hide answers.
    checklistFrozen.current = !!r0.checklist_ids
    setLoading(false)
  }

  // Save report settings: ONLY the keys changed here are sent (merged into the saved copy in one step by the
  // database), so the co-auditor's settings are never overwritten. The checklist choice is fixed by the first save.
  function setR(patch) {
    const q = queueRef.current
    if (!q) return
    if (!checklistFrozen.current && (rep.checklist_ids || []).length) q.put('rep', null, 'checklist_ids', rep.checklist_ids)
    checklistFrozen.current = true
    Object.entries(patch).forEach(([k, v]) => q.put('rep', null, k, v))
    setRep(r => ({ ...r, ...patch }))
    scheduleFlush()
  }
  // Upload mode NC / Obs counts: send ONLY the count(s) changed on this device (the database merges them into the
  // saved counts), so one auditor's NC and the other's Obs can't wipe each other out.
  function setCount(k, v) {
    const q = queueRef.current
    if (!q) return
    const pending = q.has('rep', null, 'counts') ? (q.overlayRep({}).counts || {}) : {}
    setR({ counts: { ...pending, [k]: v } })
    setRep(r => ({ ...r, counts: { ...(rep.counts || {}), [k]: v } }))
  }
  // The "send to" address is kept too (so the co-auditor or a reload doesn't lose it).
  function changeTo(v) {
    setTo(v)
    queueRef.current?.put('rep', null, 'send_to', v)
    scheduleFlush()
  }
  // "Save now": finish whatever is being typed (blur saves notes) and save everything queued. True = all saved.
  async function saveNow() {
    if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur()
    await new Promise(r => setTimeout(r, 50))   // let the blur's save land in the queue
    clearTimeout(flushTimer.current)
    return flushQueue()
  }
  // Before signing or sending: everything must be saved, and the screen must match what is saved (the co-auditor
  // may have changed something). Returns the saved job record, or null after telling the auditor why not.
  async function checkLatest() {
    setChangedRows(new Set())
    if (!(await saveNow())) {
      if (queueRef.current?.lockedHeld()) toast('Some changes are waiting', 'warning', { detail: 'The report is signed and locked — unlock it to save them, or discard them.' })
      else toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then tap Save now.' })
      return null
    }
    let s
    try { s = await fetchServer() } catch (e) { s = { j: null } }
    if (!s.j) { toast("Couldn't check for changes", 'warning', { detail: 'Check your Wi-Fi, then try again.' }); return null }
    const server = byItem(s.rs)
    const FIELDS = ['result', 'notes', 'closeout_whom', 'closed_at', 'closed_note', 'revisit']
    const rows = [...new Set([...Object.keys(server), ...Object.keys(resp)])]
      .filter(itemId => FIELDS.some(f => String(resp[itemId]?.[f] ?? '') !== String(server[itemId]?.[f] ?? '')))
    const sr = normRep(s.j.ia_report || {}, (s.j.ia_report || {}).checklist_ids || rep.checklist_ids, contactRef.current)
    // (counts are typed as text on screen and read back as numbers — compare them as numbers)
    const repChanged = ['start_at', 'mode', 'no_photo_reason', 'photo_consent', 'photo_consent_note', 'mgmt_rep', 'clean', 'closeout']
      .some(k => JSON.stringify(rep[k] ?? null) !== JSON.stringify(sr[k] ?? null))
      || ['nc', 'obs'].some(k => (+rep.counts?.[k] || 0) !== (+sr.counts?.[k] || 0))
    const filesChanged = files.map(f => f.id).sort().join() !== s.ev.map(f => f.id).sort().join()
    const moved = (s.j.ia_stage || '') !== (job.ia_stage || '') || !!s.j.ia_report_sent_at !== !!job.ia_report_sent_at
    applyServer(s)
    if (moved) { toast('This report has moved on', 'info', { detail: 'The page has been refreshed — please check it.' }); return null }
    if (rows.length || repChanged || filesChanged) {
      setChangedRows(new Set(rows))
      const other = (job.auditor2_name ? (mySlot === 'auditor2' ? job.auditor_name : job.auditor2_name) : null) || 'Someone'
      const by = s.rs.find(r => rows.includes(r.item_id) && r.updated_by && r.updated_by !== whoRef.current)?.updated_by || other
      toast(`${by} made changes`, 'warning', { detail: 'Check the updated rows (highlighted), then press the button again.' })
      return null
    }
    return s.j
  }

  const allItems = useMemo(() => lists.flatMap(l => l.items), [lists])
  const ans = useMemo(() => answerable(allItems), [allItems])
  const clauses = useMemo(() => clauseOf(allItems), [allItems])

  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>
  if (!job) return <div>Job not found.</div>

  const client = job.clients || {}
  const std = cycle?.standards || []
  const stage = job.ia_stage
  const sentBefore = !!job.ia_report_sent_at
  // stageOpen: the report can still be worked on (not submitted). frozen: the second auditor has signed — locked for
  // BOTH auditors until the lead sends it or presses "Unlock to edit" (the database enforces the same rule).
  const stageOpen = job.status !== 'Cancelled' && (!stage || stage === 'returned')
  const frozen = stageOpen && !!job.auditor2_name && (job.report_signatures || {}).auditor2?.name === job.auditor2_name
  const editable = stageOpen && !frozen
  // Standards changed by the office after this checklist was locked in → the locked checklist no longer matches.
  const sameIds = (a, b) => [...(a || [])].sort().join() === [...(b || [])].sort().join()
  const expectedIds = pickChecklists(cycle?.standards || [], library).lists.map(c => c.id)
  const checklistOutdated = checklistFrozen.current && (rep.checklist_ids || []).length > 0 && !sameIds(expectedIds, rep.checklist_ids)
  const mode = rep.mode
  const paid = !!job.invoice_cleared_confirmed
  const who = profile?.name || user?.email || 'Auditor'

  const ansItems = allItems.filter(it => ans.has(it.id))
  const answered = ansItems.filter(it => resp[it.id]?.result).length
  const counts = mode === 'upload'
    ? { nc: +rep.counts.nc || 0, obs: +rep.counts.obs || 0 }
    : countFindings(ansItems.map(it => resp[it.id]?.result))
  const autoConclusion = conclusionText(counts)   // automatic — printed under "Audit Results & Conclusion"
  const findings = ansItems.filter(it => isFinding(resp[it.id]?.result))
  // ⚑ "Revisit" marks (owner, 4 Nov 2026): the auditor's own highlight, as in the old Excel checklist. Never printed;
  // the report cannot be signed or sent while any row is still marked.
  const marked = ansItems.filter(it => resp[it.id]?.revisit).length
  const reportRef = (job.report_reference_no || job.quote_ref || '').trim()
  const photos = files.filter(f => f.evidence_type === 'session_photo')
  const hasPhoto = photos.length > 0
  // Step 1 must be complete before moving on: start time and how the audit is recorded. The session photo (or the
  // reason there is none) and its consent answer are checked only when sending the report, as bizSAFE — the auditor
  // may take the photo on their phone and upload it at the end of the audit.
  const step1Missing = !rep.start_at ? 'Set the audit start time first'
    : !mode ? 'Choose how you will record this audit' : null

  // Start again on the checklist for the current standards (answers on the old checklist stay saved but hidden).
  async function switchChecklist() {
    if (!window.confirm(`Switch to the checklist for ${standardsLabel(std)}? Answers already entered on the current checklist will no longer show.`)) return
    if (!(await saveNow())) return toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then tap Save now.' })
    const { error } = await supabase.rpc('ia_report_patch', { p_job: id, p_patch: {}, p_remove: ['checklist_ids'] })
    if (error) return failed("Couldn't switch the checklist", error.message)
    checklistFrozen.current = false
    setLoading(true); await load()
  }

  // A checklist answer: only the changed box is saved (never the whole row), so the co-auditor's result or notes on
  // the same row are never put back to an older value.
  function saveResp(itemId, patch) {
    const q = queueRef.current
    if (!q) return
    // Each box carries what was SAVED before this change, so a newer save by the co-auditor is never overwritten.
    Object.entries(patch).forEach(([k, v]) => q.put('resp', itemId, k, v, serverResp.current[itemId]?.[k] ?? null))
    if (filter === 'todo' || filter === 'revisit') keepVisible.current.add(itemId)
    setResp(r => ({ ...r, [itemId]: { ...(r[itemId] || {}), job_id: id, item_id: itemId, ...patch, updated_by: who } }))
    if (!checklistFrozen.current && stageOpen) setR({})   // first answer → freeze the checklist choice (setR also saves)
    else scheduleFlush()
  }

  // "Mark All Conform" for one checklist: every row not yet answered becomes Conform. Checked by the DATABASE, not
  // this screen: a row anyone has already answered (e.g. the co-auditor's NC a moment ago) is never touched.
  async function markAllConform(list) {
    const todo = list.items.filter(it => ans.has(it.id) && !resp[it.id]?.result)
    if (!todo.length) return toast('Nothing left to fill', 'info', { detail: 'Every row already has an answer' })
    if (!window.confirm(`Mark ${todo.length} unanswered row${todo.length === 1 ? '' : 's'} in "${list.title}" as Conform?\n\nRows already answered (by you or your co-auditor) are not changed.`)) return
    if (!(await saveNow())) return toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then tap Save now.' })
    if (!checklistFrozen.current) { setR({}); await saveNow() }
    setSaveState('saving')
    const ids = todo.map(it => it.id)
    // 1) rows that don't exist yet are created as Conform (an existing row is skipped, never overwritten);
    // 2) rows that exist but have no result yet become Conform.
    const { error: e1 } = await supabase.from('ia_responses')
      .upsert(ids.map(itemId => ({ job_id: id, item_id: itemId, result: 'Conform', updated_by: who })), { onConflict: 'job_id,item_id', ignoreDuplicates: true })
    const { error: e2 } = e1 ? { error: null } : await supabase.from('ia_responses')
      .update({ result: 'Conform', updated_by: who }).eq('job_id', id).in('item_id', ids).is('result', null)
    setSaveState('saved')
    if (e1 || e2) failed('Not saved', (e1 || e2).message)
    // Show what is saved now (not via refresh(), which skips if a background refresh is already running and could
    // then land an older copy — leaving these rows blank and a later answer wrongly seen as a clash).
    const s = await fetchServer().catch(() => null)
    if (s?.j) applyServer(s)
  }

  // Session photo (Step 1) — same record as bizSAFE (audit_evidence_files 'session_photo'); kept in AuditPM, not
  // printed in the report.
  async function uploadPhoto(picked) {
    if (!picked || busy) return   // busy: a second tap while uploading never uploads twice
    // iPhone photos picked on Windows can arrive with no type — accept them by their file name.
    if (!/^image\//.test(picked.type) && !/\.(jpe?g|png|heic|heif|webp)$/i.test(picked.name)) return toast('Choose a photo', 'warning')
    setBusy('upload')
    const file = await shrinkPhoto(picked)
    const path = `${id}/ia_photo_${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`
    const up = await supabase.storage.from('audit-evidence').upload(path, file)
    if (up.error) { setBusy(false); return failed('Upload failed', up.error.message) }
    const { data, error } = await supabase.from('audit_evidence_files').insert({ job_id: id, storage_path: path, file_name: file.name, mime: file.type, size_bytes: file.size, evidence_type: 'session_photo', title: file.name, is_annex: false }).select().single()
    setBusy(false)
    if (error) return failed("Couldn't save", error.message)
    setFiles(fs => [...fs, data])
  }

  async function upload(file, type) {
    if (!file) return
    if (!/\.pdf$/i.test(file.name)) return toast('Upload a PDF', 'warning')
    setBusy('upload')
    // The report attaches this file as Annex I, so it must open as a normal PDF (no password) — check now, not
    // when the report is being prepared.
    try {
      const { PDFDocument } = await import('pdf-lib')
      await PDFDocument.load(await file.arrayBuffer())
    } catch (e) {
      setBusy(false)
      return toast("This PDF can't be opened", 'warning', { detail: 'It may have a password. Save it again without one, then upload.' })
    }
    const path = `${id}/ia_${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`
    const up = await supabase.storage.from('audit-evidence').upload(path, file)
    if (up.error) { setBusy(false); return failed('Upload failed', up.error.message) }
    const { data, error } = await supabase.from('audit_evidence_files').insert({ job_id: id, storage_path: path, file_name: file.name, mime: file.type, size_bytes: file.size, evidence_type: type, title: file.name, is_annex: true }).select().single()
    if (error) { setBusy(false); return failed("Couldn't save", error.message) }
    // Only OLDER checklist PDFs are replaced — if both auditors upload at the same moment, the newest one stays.
    if (type === 'ia_checklist') await supabase.from('audit_evidence_files').update({ archived_at: new Date().toISOString() }).eq('job_id', id).eq('evidence_type', 'ia_checklist').is('archived_at', null).lt('created_at', data.created_at)
    setFiles(fs => [...fs.filter(f => type !== 'ia_checklist' || f.evidence_type !== 'ia_checklist'), data])
    setBusy(false)
  }
  async function removeFile(f) {
    if (!window.confirm(`Remove "${f.file_name}"?`)) return
    await supabase.from('audit_evidence_files').update({ archived_at: new Date().toISOString() }).eq('id', f.id)
    setFiles(fs => fs.filter(x => x.id !== f.id))
  }
  async function openFile(f) {
    const { data } = await supabase.storage.from('audit-evidence').createSignedUrl(f.storage_path, 600)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }
  async function viewUpload() {
    try { const d = await iaAction({ action: 'view-upload', jobId: id }); window.open(d.url, '_blank') } catch (e) { failed("Couldn't open the file", e.message) }
  }

  // E-signatures (same record and same rule as bizSAFE: jobs.report_signatures[slot] = { name, signed_at, ip }).
  // Two auditors: the second auditor signs first in their own login (the report then locks); the lead auditor then
  // sends (and signs at send). Signatures are written by the server (ia-actions 'sign'), which also records the IP.
  const slots = [['auditor1', job.auditor_name], ['auditor2', job.auditor2_name]].filter(([, n]) => n)
  const sigOf = (slot, name, all) => { const g = (all || job.report_signatures || {})[slot]; return g && g.name === name ? g : null }
  const hasSecond = !!job.auditor2_name
  const isSecond = mySlot === 'auditor2'
  const isLead = mySlot === 'auditor1'
  async function signMine() {
    const d = await iaAction({ action: 'sign', jobId: id })
    setJob(j => ({ ...j, report_signatures: d.signatures || {} }))
    return d.signatures || {}
  }
  // Second auditor: "Sign report" (only once everything is complete, as bizSAFE). Signing locks the report.
  async function signReport() {
    const p = problems()
    if (p.length) return toast('Fill in the missing items', 'warning', { detail: '• ' + p.join('\n• ') })
    setBusy('sign')
    const fresh = await checkLatest()
    if (!fresh) { setBusy(false); return }
    if (!window.confirm(`Sign this report?\n\nIt will then be locked — ${job.auditor_name} (lead) will send it. If anything needs changing, the lead can unlock it and you will be asked to sign again.`)) { setBusy(false); return }
    try { await signMine(); toast('Signed', 'success', { detail: 'The report is locked. The lead auditor will send it.' }) } catch (e) { failed("Couldn't sign", e.message) }
    setBusy(false)
  }
  // Lead (or office): unlock a signed report to change it. The second auditor's signature is removed and they are
  // emailed to check and sign again.
  async function unlock() {
    if (!window.confirm(`Unlock the report to make changes?\n\n${job.auditor2_name}'s signature will be removed, and they will be asked to check the report and sign again.`)) return
    setBusy('unlock')
    try {
      const d = await iaAction({ action: 'unlock', jobId: id })
      setJob(j => ({ ...j, report_signatures: d.signatures || {} }))
      toast('Report unlocked', 'success', { detail: `${job.auditor2_name} has been asked to sign again` })
      flushQueue()   // changes that were waiting because of the lock are saved now
    } catch (e) { failed("Couldn't unlock the report", e.message) }
    setBusy(false)
  }

  // sigs: signatures to print. For a preview, the sender's own signature shows as "now" (IP added at send).
  function header(sigs, previewMine) {
    const all = sigs || job.report_signatures || {}
    return {
      company: client.name, address: client.address, auditDate: job.audit_date, timeOfAudit: startTimeText(rep.start_at),
      // Report ref: the office's Report Ref No. when typed (jobs signed outside AuditPM), else the quote number — as bizSAFE.
      location: oneLine(job.audit_site_address), mgmtRep: (rep.mgmt_rep || '').trim(), refNo: reportRef,
      auditors: slots.map(([, n]) => n), standards: std,
      signatures: slots.map(([slot, name]) => {
        const g = all[slot] && all[slot].name === name ? all[slot] : null
        if (g) return { name, signed_at: g.signed_at, ip: g.ip || null }
        if (previewMine && slot === mySlot) return { name, signed_at: new Date().toISOString(), ip: null }
        return { name }
      }),
    }
  }
  function snapshots(kinds, sigs, previewMine) {
    const h = header(sigs, previewMine)
    const checklist = files.filter(f => f.evidence_type === 'ia_checklist')
    const others = files.filter(f => f.evidence_type === 'ia_other')
    const common = { header: h, mode, lists, responses: resp, counts, conclusion: autoConclusion,
      attachments: [...checklist, ...others].map(f => ({ path: f.storage_path, name: f.file_name })) }
    return Object.fromEntries(kinds.map(k => [k, buildSnapshot(k, common)]))
  }

  function problems() {
    const p = []
    if (!job.audit_date) p.push('The audit date is not set (office).')
    if (!(job.audit_site_address || '').trim()) p.push('The audit location is not set — please ask the office to fill it in on the job page')
    if (!rep.start_at) p.push('Audit start time (Step 1)')
    if (!hasPhoto && !(rep.no_photo_reason || '').trim()) p.push('Session photo, or the reason there is none (Step 1)')
    // Not for an updated re-send: reports sent before the consent question existed must stay re-sendable.
    if (hasPhoto && !rep.photo_consent && !sentBefore) p.push('Photo consent — did everyone in the photo agree to it? (Step 1)')
    if (hasPhoto && rep.photo_consent === 'No' && !String(rep.photo_consent_note || '').trim() && !sentBefore)
      p.push('Photo consent is “No” — remove the photo, or say why it is kept (Step 1)')
    if (!reportRef) p.push('Report Ref No. — please ask the office to fill it in on the job page')
    if (!(rep.mgmt_rep || '').trim()) p.push('Name of Management Representative')
    if (!to.trim() || !/^\S+@\S+\.\S+$/.test(to.trim())) p.push('Client email address')
    if (mode === 'upload') {
      if (!files.some(f => f.evidence_type === 'ia_checklist')) p.push('Upload the completed checklist (PDF)')
    } else {
      if (answered < ansItems.length) p.push(`${ansItems.length - answered} checklist item(s) not answered yet`)
      const noNote = findings.filter(it => !String(resp[it.id]?.notes || '').trim()).length
      if (noNote) p.push(`${noNote} finding(s) without notes`)
      if (marked) p.push(`${marked} checklist row(s) still marked ⚑ to revisit — check them and remove the marks`)
    }
    return p
  }

  const sendKinds = () => sentBefore ? (job.ia_report?.sent_kinds || ['client']).filter(k => k !== 'closeout') : ['client', ...(rep.clean && mode !== 'upload' ? ['clean'] : [])]
  function openPreview(kinds) {
    const snaps = kinds[0] === 'closeout'
      ? { closeout: buildSnapshot('closeout', { header: header({}, true), mode, lists, responses: resp, counts, conclusion: autoConclusion, closeoutDate: findings.map(it => resp[it.id]?.closed_at).filter(Boolean).sort().pop() }) }
      : snapshots(kinds, null, true)
    setPreview({ snaps, kind: kinds[0] })
  }
  // Once sent, the work is done — back to My Audits (office staff: back to the job), as bizSAFE after submission.
  function leave() { setPreview(null); setBusy(false); navigate(isAuditor ? '/my-audits' : `/jobs/${id}`) }
  // Lead auditor sends (as bizSAFE). Everything must be saved and match what is saved (the co-auditor may have changed
  // something), and the second auditor must have signed. The report is built from that checked copy.
  async function send() {
    if (!isLead) return toast('Only the lead auditor sends the report', 'info', { detail: `${job.auditor_name || 'The lead auditor'} sends it from their own login` })
    const p = problems()
    if (p.length) return toast('Fill in the missing items', 'warning', { detail: '• ' + p.join('\n• ') })
    setBusy('send')
    const fresh = await checkLatest()
    if (!fresh) { setBusy(false); return }
    if (hasSecond && !sigOf('auditor2', job.auditor2_name, fresh.report_signatures || {})) { setBusy(false); return toast(`${job.auditor2_name} needs to sign first`, 'warning') }
    const kinds = sendKinds()
    const msg = sentBefore
      ? `Send the updated report(s) to ${to.trim()}? The client’s existing links will show the new version.`
      : paid
        ? `Send the ${kinds.length > 1 ? 'Company report and Audit report' : 'Company report'} to ${to.trim()} now?`
        : 'Payment has not been recorded yet. Accounts will be told, and the report will be sent once payment is received.\n\nContinue?'
    if (!window.confirm(msg)) { setBusy(false); return }
    try {
      const sigs = await signMine()
      if (sentBefore) {
        await iaAction({ action: 'resend-updated', jobId: id, snapshots: snapshots(kinds, sigs), to: to.trim() })
        toast('Updated report sent', 'success')
      } else {
        const d = await iaAction({ action: 'submit', jobId: id, snapshots: snapshots(kinds, sigs), to: to.trim(), closeoutRequired: mode !== 'upload' && !!rep.closeout })
        if (d.status === 'sent') toast('Report sent to the client', 'success')
        else toast('Sent to Accounts', 'success', { detail: 'It goes out once payment is received' })
      }
      leave(); return
    } catch (e) { failed("Couldn't send the report", e.message); refresh() }   // show where it stands now
    setBusy(false)
  }

  // Close-out Report: sent by the lead; it carries ONLY the sender's signature (the second auditor did not sign it).
  async function sendCloseout() {
    if (!isLead) return toast('Only the lead auditor sends the Close-out Report', 'info', { detail: `${job.auditor_name || 'The lead auditor'} sends it from their own login` })
    const open = findings.filter(it => !resp[it.id]?.closed_at)
    if (open.length) return toast('Close all findings first', 'warning', { detail: `${open.length} still open` })
    const dest = to.trim()
    if (!dest) return toast("Enter the client's email", 'warning')
    setBusy('send')
    const fresh = await checkLatest()
    if (!fresh) { setBusy(false); return }
    if (!window.confirm(`Send the Close-out Report to ${dest}?`)) { setBusy(false); return }
    try {
      const latest = findings.map(it => resp[it.id].closed_at).sort().pop()
      const sigs = await signMine()
      const snap = buildSnapshot('closeout', { header: header({ auditor1: sigs.auditor1 }), mode, lists, responses: resp, counts, conclusion: autoConclusion, closeoutDate: latest })
      await iaAction({ action: 'send-closeout', jobId: id, snapshot: snap, to: dest })
      toast('Close-out Report sent', 'success'); leave(); return
    } catch (e) { failed("Couldn't send the Close-out Report", e.message); refresh() }
    setBusy(false)
  }

  const visible = it => {
    if (filter === 'all') return true
    if (!ans.has(it.id)) return filter === 'all'
    if (filter === 'todo') return !resp[it.id]?.result || keepVisible.current.has(it.id)   // stays until the filter is pressed again
    if (filter === 'findings') return isFinding(resp[it.id]?.result)
    if (filter === 'revisit') return !!resp[it.id]?.revisit || keepVisible.current.has(it.id)
    return true
  }
  const changeFilter = k => { keepVisible.current = new Set(); setFilter(k) }
  const lastOf = it => hist.byId[it.id] || hist.byText[norm(`${it.label || ''} ${it.body || ''}`)] || null

  return (
    <div style={{ maxWidth: 1150 }}>
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 4 }}>
            <span style={{ cursor: 'pointer' }} onClick={() => navigate(isAuditor ? '/my-audits' : `/jobs/${id}`)}>{isAuditor ? 'My Audits' : 'Job'}</span> /
          </div>
          <h1 style={{ marginBottom: 4 }}>Internal Audit — {client.name}</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>{standardsLabel(std) || '—'} · {job.audit_date ? longDate(job.audit_date) : 'date not set'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {job.ia_plan_token && <button className="sm" onClick={() => window.open(`/ia-plan/${job.ia_plan_token}`, '_blank')}>Audit plan</button>}
          {cycle?.upload_path && <button className="sm" onClick={viewUpload}>Client’s external audit report</button>}
        </div>
      </div>

      {!linked && <div style={{ background: '#fdecec', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
        <b>Your login isn’t linked to this audit.</b> Nothing you enter here can be saved. Please ask the office to link your AuditPM login to your auditor record (Personnel), then reload this page.</div>}
      {frozen && <div style={{ background: '#EAF3DE', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, color: '#27500a' }}>
        <b>Signed by {job.auditor2_name} — the report is locked.</b>{' '}
        {isLead ? 'Check it in Step 3 and send it. To change anything, press “Unlock to edit” there (they will be asked to sign again).'
          : isSecond ? `${job.auditor_name} (lead) will send it. If something needs changing, ask them to unlock it.`
          : `${job.auditor_name} (lead) will send it.`}</div>}
      {stage === 'returned' && <div style={{ background: '#fdecec', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}><b>Returned for revision:</b> {job.ia_return_reason}</div>}
      {job.status === 'Cancelled' && <Note>This job has been cancelled.</Note>}
      {stage === 'awaiting_payment' && <Note>Submitted. The report will be sent to the client once Accounts record the payment.</Note>}
      {stage === 'sending' && <Note>Sending the report to the client…</Note>}
      {stage === 'awaiting_closeout' && step !== 3 && (
        <div style={{ background: '#fff4e0', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          The report has been sent, so the checklist is locked. Record how each finding was closed in Step 3.
          <button className="sm primary" onClick={() => setStep(3)}>Go to close-out →</button>
        </div>)}
      {stage === 'done' && <Note>Report sent to {job.ia_report?.last_to} on {longDate(String(job.ia_report_sent_at).slice(0, 10))}. This audit is complete.</Note>}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {STEPS.map((s, i) => {
          const n = i + 1
          return (
            <button key={s} onClick={() => setStep(n)} className="sm"
              style={{ fontWeight: step === n ? 600 : 400, background: step === n ? 'var(--text)' : 'var(--bg)', color: step === n ? 'var(--bg)' : 'var(--text)' }}>
              {n}. {n === 3 && stage === 'awaiting_closeout' ? 'Close-out' : s}
            </button>
          )
        })}
      </div>

      {/* ── Step 1: Start ─────────────────────────────────────────── */}
      {step === 1 && (
        <div>
          <div className="card" style={{ marginBottom: 12 }}>
            <h2 className="card-title">Audit start</h2>
            <div className="field" style={{ maxWidth: 340 }}>
              <label>Audit start date &amp; time</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="datetime-local" value={toLocalInput(rep.start_at)} disabled={!editable}
                  onChange={e => { setR({ start_at: e.target.value ? new Date(e.target.value).toISOString() : null }) }} />
                <button type="button" className="sm" disabled={!editable} onClick={() => setR({ start_at: new Date().toISOString() })}>Now</button>
              </div>
            </div>
            {rep.start_at && job.audit_date && sgDay(rep.start_at) !== String(job.audit_date).slice(0, 10) && (
              <div style={{ fontSize: 13, color: '#854F0B', marginTop: 4 }}>The start date is not the audit date ({longDate(job.audit_date)}) — please check.</div>)}
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {editable && <label style={{ cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, pointerEvents: busy ? 'none' : undefined, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' }}>
                Upload session photo<input type="file" accept="image/*,.heic,.heif" disabled={!!busy} style={{ display: 'none' }} onChange={e => { uploadPhoto(e.target.files[0]); e.target.value = '' }} /></label>}
              {editable && <TakePhotoButton disabled={!!busy} onPhoto={uploadPhoto} />}
              {!hasPhoto && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text2)' }}>
                  <span>Provide reason if no session photo is available:</span>
                  <input value={rep.no_photo_reason || ''} placeholder="Reason" disabled={!editable} onChange={e => setR({ no_photo_reason: e.target.value })}
                    style={{ fontSize: 13, maxWidth: 240, ...(!(rep.no_photo_reason || '').trim() ? { borderColor: 'var(--danger)' } : {}) }} />
                </div>
              )}
            </div>
            {hasPhoto && (
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {photos.map(f => <span key={f.id} style={{ fontSize: 13, background: 'var(--bg2)', borderRadius: 6, padding: '2px 6px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={() => openFile(f)}>{f.file_name}</span>
                  {editable && <button className="sm danger" style={{ padding: '0 4px', marginLeft: 4 }} onClick={() => removeFile(f)}>×</button>}</span>)}
              </div>
            )}
            {/* Photo consent (as bizSAFE's per-attendee tick): kept in AuditPM for the office, never in the report. */}
            {hasPhoto && (
              <div style={{ marginTop: 10, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                <span style={!rep.photo_consent ? { color: 'var(--danger)' } : undefined}>Did everyone in the photo agree to it being taken?</span>
                {['Yes', 'No'].map(v => (
                  <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: editable ? 'pointer' : 'default', margin: 0 }}>
                    <input type="radio" name="ia-photo-consent" checked={rep.photo_consent === v} disabled={!editable}
                      onChange={() => setR({ photo_consent: v })} style={{ width: 'auto', margin: 0 }} /> {v}
                  </label>))}
              </div>
            )}
            {hasPhoto && rep.photo_consent === 'No' && (
              <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text2)', flexWrap: 'wrap' }}>
                <span>Not everyone agreed — remove the photo, or say why it is kept:</span>
                <input value={rep.photo_consent_note || ''} placeholder="Reason" disabled={!editable} onChange={e => setR({ photo_consent_note: e.target.value })}
                  style={{ fontSize: 13, maxWidth: 280, ...(!String(rep.photo_consent_note || '').trim() ? { borderColor: 'var(--danger)' } : {}) }} />
              </div>
            )}
          </div>

          <div className="card">
            <h2 className="card-title">How will you record this audit?</h2>
            {missing.length > 0 && <Note>No AuditPM checklist for {missing.join(', ')} — please use “Upload my checklist”.</Note>}
            {/* Two big buttons, always shown — the chosen one is highlighted; click the other to switch. */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {(lists.length > 0 && !missing.length || mode === 'worksheet') && <BigChoice title="Fill in on AuditPM" text="Use the checklist in Step 2 (recommended)."
                selected={mode === 'worksheet'} disabled={!editable} onClick={() => setR({ mode: 'worksheet' })} />}
              <BigChoice title="Upload my checklist" text="I filled in the Excel / paper checklist. Upload it as PDF in Step 2."
                selected={mode === 'upload'} disabled={!editable} onClick={() => setR({ mode: 'upload' })} />
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Checklist ─────────────────────────────────────── */}
      {step === 2 && !mode && <Note>Choose how you will record this audit in Step 1 first.</Note>}
      {step === 2 && editable && mode === 'worksheet' && checklistOutdated && (
        <div style={{ background: '#fff4e0', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          The standards for this audit were changed to {standardsLabel(std) || '—'} after this checklist was started. The report will show the new standards.
          <button className="sm" onClick={switchChecklist}>Switch checklist</button>
        </div>)}
      {step === 2 && mode === 'worksheet' && (
        <div className="card" style={{ marginBottom: 14, padding: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 2 }}>
            {[['all', 'All'], ['todo', 'Not answered'], ['findings', 'Findings'], ['revisit', '⚑ Marked to revisit']].map(([k, l]) => <button key={k} className={`sm${filter === k ? ' primary' : ''}`} onClick={() => changeFilter(k)}>{l}</button>)}
            <span style={{ fontSize: 13, color: 'var(--text2)', marginLeft: 6 }}>{answered} of {ansItems.length} answered · {counts.nc} NC · {counts.obs} Obs</span>
            {/* As bizSAFE: a red count of what is still open; each open row carries a red dot. */}
            {ansItems.length - answered > 0 && <span style={{ fontSize: 13, color: 'var(--danger)', fontWeight: 600 }}>● {ansItems.length - answered} not answered</span>}
            {marked > 0 && <span style={{ fontSize: 13, color: '#854F0B', fontWeight: 600 }}>⚑ {marked} marked</span>}
            {hist.date && <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 'auto' }}>Last audit: {longDate(hist.date)}</span>}
          </div>
          {lists.map(l => (
            <div key={l.id}>
              <div style={{ padding: '8px 14px', fontWeight: 700, fontSize: 13, background: 'var(--bg2)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>{l.title}</span>
                {editable && <button className="sm" style={{ marginLeft: 'auto', fontWeight: 600 }} onClick={() => markAllConform(l)}>Mark All Conform (only unfilled rows)</button>}
              </div>
              {l.items.filter(visible).map((it, idx, arr) => {
                if (!ans.has(it.id)) {
                  if (it.kind === 'note') return <div key={it.id} style={{ padding: '2px 14px 2px 40px', fontSize: 13, fontStyle: 'italic', color: 'var(--text3)' }}>{it.body}</div>
                  const soaStart = it.section === 'soa' && (idx === 0 || arr[idx - 1].section !== 'soa')
                  return <React.Fragment key={it.id}>
                    {soaStart && <div style={{ padding: '8px 14px', fontWeight: 700, fontSize: 13, background: 'var(--bg2)' }}>Statement of Applicability (Annex A)</div>}
                    <div style={{ padding: '6px 14px', fontWeight: 700, fontSize: it.kind === 'section' ? 14 : 13, borderTop: '1px solid var(--border)', background: it.kind === 'section' ? '#e0f2f2' : undefined }}>{it.clause_no} {it.body}</div>
                  </React.Fragment>
                }
                return <Row key={it.id} it={it} clause={clauses[it.id]} r={resp[it.id]} last={lastOf(it)} disabled={!editable} onSave={saveResp}
                  me={who} highlight={changedRows.has(it.id)} reset={resetAt[it.id]} />
              })}
            </div>))}
        </div>)}
      {step === 2 && mode === 'upload' && (
        <div className="card" style={{ marginBottom: 14 }}>
          <h2 className="card-title">Your completed checklist</h2>
          <FileSlot label="Completed checklist (PDF)" files={files.filter(f => f.evidence_type === 'ia_checklist')} disabled={!editable || busy} onPick={f => upload(f, 'ia_checklist')} onOpen={openFile} onRemove={removeFile} />
          <FileSlot label="Other documents (PDF, if any)" files={files.filter(f => f.evidence_type === 'ia_other')} disabled={!editable || busy} onPick={f => upload(f, 'ia_other')} onOpen={openFile} onRemove={removeFile} multiple />
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
            {[['nc', 'NC'], ['obs', 'Observations']].map(([k, l]) => (
              <div key={k} className="field" style={{ width: 130 }}><label>{l}</label>
                <input type="number" min="0" disabled={!editable} value={rep.counts[k]} onChange={e => setCount(k, e.target.value)} onWheel={e => e.currentTarget.blur()} /></div>))}
          </div>
        </div>)}

      {/* ── Step 3: Report ────────────────────────────────────────── */}
      {step === 3 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <h2 className="card-title">{stage === 'awaiting_closeout' ? 'Close-out' : 'Report'}</h2>
          {!stageOpen && stage !== 'awaiting_closeout' && <div style={{ fontSize: 13, color: 'var(--text2)' }}>This report has been submitted — no changes can be made unless the office returns it to you.</div>}
          {stageOpen && <>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="field"><label>Name of Management Representative</label><input value={rep.mgmt_rep || ''} disabled={!editable} onChange={e => setR({ mgmt_rep: e.target.value })} />
                <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 3 }}>Filled in from the client contact — please confirm with the client and change if needed.</div></div>
              <div className="field"><label>Send the report to (client email)</label><input value={to} disabled={!isLead && !editable} onChange={e => changeTo(e.target.value)} /></div>
            </div>
            {!sentBefore && <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, margin: '6px 0 12px' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked disabled style={{ width: 'auto' }} /> Company report</label>
              {mode !== 'upload' && <>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: editable ? 'pointer' : 'default' }}><input type="checkbox" style={{ width: 'auto' }} disabled={!editable} checked={!!rep.clean} onChange={e => setR({ clean: e.target.checked })} /> Audit report for the certification body (no Obs / NC marks)</label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: editable ? 'pointer' : 'default' }}><input type="checkbox" style={{ width: 'auto' }} disabled={!editable} checked={!!rep.closeout} onChange={e => setR({ closeout: e.target.checked })} /> Client needs a Close-out Report later</label>
              </>}
            </div>}
            <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 10 }}>
              {slots.map(([slot, name]) => { const g = sigOf(slot, name); return (
                <div key={slot}>{name}{slot === 'auditor1' && hasSecond ? ' (lead)' : ''}: {g ? <span><span style={{ color: 'var(--brand)' }}>✓</span> signed {new Date(g.signed_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}{g.ip ? ` · IP ${g.ip}` : ''}</span>
                  : slot === 'auditor1' ? <span>signs when the report is sent</span>
                  : <span style={{ color: 'var(--text3)' }}>not signed yet{slot !== mySlot ? ' (signs in their own login)' : ''}</span>}</div>) })}
            </div>
            {isSecond ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="sm" disabled={!!busy} onClick={() => openPreview(sendKinds())}>👁 Preview report</button>
                {sigOf('auditor2', job.auditor2_name)
                  ? <span style={{ fontSize: 13, color: 'var(--text2)' }}>✓ You have signed — the report is locked. {job.auditor_name} (lead) will send it.</span>
                  : <button className="primary" disabled={!!busy || pendingCount > 0} title={pendingCount ? 'Changes not saved yet' : undefined} onClick={signReport}>{busy === 'sign' ? 'Checking…' : 'Sign report'}</button>}
              </div>
            ) : isLead ? <>
              {hasSecond && !sigOf('auditor2', job.auditor2_name) && <div style={{ fontSize: 13, color: 'var(--text2)', background: 'var(--bg2)', padding: '8px 12px', borderRadius: 8, marginBottom: 10 }}>
                Waiting for {job.auditor2_name} to sign the report before it can be sent. When you have both finished, ask them to sign.</div>}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="sm" disabled={!!busy} onClick={() => openPreview(sendKinds())}>👁 Preview report</button>
                <button className="primary" disabled={!!busy || pendingCount > 0 || (hasSecond && !sigOf('auditor2', job.auditor2_name))} title={pendingCount ? 'Changes not saved yet' : undefined} onClick={send}>
                  {busy === 'send' ? 'Sending…' : sentBefore ? 'Send updated reports' : paid ? 'Send reports to client' : 'Send to Accounts'}
                </button>
                {frozen && <button className="sm" disabled={!!busy} onClick={unlock}>{busy === 'unlock' ? 'Unlocking…' : 'Unlock to edit'}</button>}
                {!sentBefore && !paid && <span style={{ fontSize: 13, color: 'var(--text3)' }}>Payment not recorded yet — Accounts will send it once paid.</span>}
              </div>
            </> : (
              // Office staff (or anyone not on this audit): the lead auditor sends from their own login, as bizSAFE.
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="sm" disabled={!!busy} onClick={() => openPreview(sendKinds())}>👁 Preview report</button>
                {frozen && <button className="sm" disabled={!!busy} onClick={unlock}>{busy === 'unlock' ? 'Unlocking…' : 'Unlock to edit'}</button>}
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{job.auditor_name || 'The lead auditor'} (lead) sends the report from their own login.</span>
              </div>
            )}
          </>}

          {stage === 'awaiting_closeout' && (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>When the client’s close-out evidence is acceptable, mark each finding closed, then send the Close-out Report.</div>
              {!findings.length && <Note>This audit has no Obs / NC findings to close. Ask the office to mark “Close-out not needed” on the job page.</Note>}
              {findings.map(it => <CloseRow key={it.id} it={it} clause={clauses[it.id]} r={resp[it.id]} who={who} onSave={saveResp} reset={resetAt[it.id]} />)}
              <div className="field" style={{ maxWidth: 420, marginTop: 10 }}><label>Send the Close-out Report to</label><input value={to} onChange={e => changeTo(e.target.value)} /></div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="sm" disabled={!!busy} onClick={() => openPreview(['closeout'])}>👁 Preview Close-out Report</button>
                {isLead
                  ? <button className="primary" disabled={!!busy || pendingCount > 0} onClick={sendCloseout}>{busy === 'send' ? 'Sending…' : 'Send Close-out Report'}</button>
                  : <span style={{ fontSize: 13, color: 'var(--text2)' }}>{job.auditor_name || 'The lead auditor'} (lead) sends the Close-out Report.</span>}
              </div>
            </div>)}
        </div>)}

      {/* Bottom bar — as bizSAFE: Back · sync status · Save now · Next. Stays in view: unsaved changes are never hidden. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 8, flexWrap: 'wrap',
        position: 'sticky', bottom: 0, background: 'var(--bg)', padding: '10px 0', borderTop: '1px solid var(--border)', zIndex: 3 }}>
        <button className="sm" disabled={step === 1} onClick={() => setStep(s => Math.max(1, s - 1))}>← Back</button>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {busy && <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{busy === 'upload' ? '⬆ Uploading… please wait' : busy === 'sign' ? 'Checking and signing… please wait' : busy === 'unlock' ? 'Unlocking…' : 'Sending… please wait'}</span>}
          <span style={{ fontSize: 13, fontWeight: 600, color: pendingCount && saveState !== 'saving' ? 'var(--danger)' : saveState === 'saved' ? '#2e7d32' : 'var(--text3)' }}>
            {saveState === 'saving' ? 'Saving…'
              : pendingCount && queueRef.current?.lockedHeld() ? `${pendingCount} change${pendingCount === 1 ? '' : 's'} waiting — the report is locked`
              : pendingCount ? `${pendingCount} change${pendingCount === 1 ? '' : 's'} not saved — retrying` : '✓ Synced'}
          </span>
          {pendingCount > 0 && saveState !== 'saving' && <button className="sm" onClick={discardUnsaved}>Discard</button>}
          <button className="sm" onClick={async () => { if (!(await saveNow())) toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then tap Save now.' }) }} disabled={saveState === 'saving'}>Save now</button>
          {step < 3 && (() => {
            const why = step === 1 && editable ? step1Missing : null
            const blocked = !!why || saveState === 'saving'
            return <button className="primary sm" disabled={blocked} title={why || undefined} onClick={() => setStep(s => Math.min(3, s + 1))}>Next: {STEPS[step]} →</button>
          })()}
        </div>
      </div>

      {preview && <ReportPreview preview={preview} logos={logos} onKind={k => setPreview(p => ({ ...p, kind: k }))} onClose={() => setPreview(null)} />}
    </div>
  )
}

const Note = ({ children }) => <div style={{ background: 'var(--bg2)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{children}</div>

function BigChoice({ title, text, onClick, selected, disabled }) {
  return (
    <button onClick={selected || disabled ? undefined : onClick} disabled={disabled && !selected}
      style={{ flex: '1 1 260px', textAlign: 'left', padding: '16px 18px', borderRadius: 10, cursor: selected || disabled ? 'default' : 'pointer',
        border: selected ? '2px solid var(--brand)' : '1px solid var(--border)', background: selected ? 'var(--brand-light)' : 'var(--bg)' }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: selected ? 'var(--brand-dark)' : undefined }}>{selected ? '✓ ' : ''}{title}</div>
      <div style={{ fontSize: 13, color: 'var(--text2)' }}>{text}</div>
    </button>
  )
}

function FileSlot({ label, files, disabled, onPick, onOpen, onRemove, multiple }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>{label}</div>
      {files.map(f => (
        <div key={f.id} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <span style={{ color: 'var(--brand)', cursor: 'pointer' }} onClick={() => onOpen(f)}>📄 {f.file_name}</span>
          {!disabled && <button className="sm" onClick={() => onRemove(f)}>×</button>}
        </div>))}
      {!disabled && (multiple || !files.length
        ? <input type="file" accept="application/pdf,.pdf" onChange={e => { onPick(e.target.files[0]); e.target.value = '' }} style={{ fontSize: 13 }} />
        : <label style={{ fontSize: 13, color: 'var(--brand)', cursor: 'pointer' }}>Replace<input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={e => { onPick(e.target.files[0]); e.target.value = '' }} /></label>)}
    </div>
  )
}

// A typed field on a checklist row. Saves while typing (about a second after the last key) and on leaving the box —
// never lost if the page is closed mid-word.
// The co-auditor's newer text replaces what is shown only while this box is NOT being edited here — so the 20-second
// refresh never wipes what someone is typing. (`lastSaved` = the value this box last took from the saved copy.)
function useRowText(r, field, itemId, onSave, disabled, reset) {
  const saved = r?.[field] || ''
  const [text, setText] = useState(saved)
  const lastSaved = useRef(saved)
  const editing = useRef(false)
  useEffect(() => {
    if (saved === lastSaved.current) return
    if (!editing.current || text === lastSaved.current) setText(saved)
    lastSaved.current = saved
  }, [saved])
  // The co-auditor's text won a clash on this row: show theirs now, even mid-typing (the toast shows what was typed),
  // so leaving the box can't quietly put ours back over theirs.
  useEffect(() => {
    if (!reset || reset.field !== field) return
    editing.current = false
    lastSaved.current = saved
    setText(saved)
  }, [reset])
  useEffect(() => {
    if (text === saved || disabled) return
    const t = setTimeout(() => onSave(itemId, { [field]: text }), 900)
    return () => clearTimeout(t)
  }, [text])
  const flush = () => { editing.current = false; if (text !== saved) onSave(itemId, { [field]: text }) }
  const change = v => { editing.current = true; setText(v) }
  return [text, change, flush]
}

// One checklist row: requirement · last audit · this audit (Obs / NC result, findings / notes, close-out whom / date —
// the same order as the report's Annex I columns). A row last changed by the co-auditor shows "name · time".
const Row = React.memo(function Row({ it, clause, r, last, disabled, onSave, me, highlight, reset }) {
  const [notes, setNotes, flushNotes] = useRowText(r, 'notes', it.id, onSave, disabled, reset)
  const [whom, setWhom, flushWhom] = useRowText(r, 'closeout_whom', it.id, onSave, disabled, reset)
  const text = it.kind === 'control' ? `${it.title ? it.title + ' — ' : ''}${it.body || ''}` : `${it.label ? it.label + ' ' : ''}${it.body || ''}`
  const byOther = r?.updated_by && r.updated_by !== me && r.updated_at
  const revisit = !!r?.revisit
  return (
    <div className="ia-row" style={{ gap: 10, padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 13,
      background: highlight ? '#fff4d6' : revisit ? '#fff7c2' : undefined, boxShadow: revisit ? 'inset 4px 0 0 #E6A817' : undefined }}>
      <div>
        {/* ⚑ the auditor's "revisit" highlight (never printed). Saved as true / cleared to empty. */}
        <button type="button" className="sm" disabled={disabled} title={revisit ? 'Remove the revisit mark' : 'Mark this row to revisit before sending'}
          onClick={() => onSave(it.id, { revisit: revisit ? null : true })}
          style={{ padding: '0 6px', marginRight: 6, lineHeight: '20px', color: revisit ? '#854F0B' : 'var(--text3)', background: revisit ? '#ffe9a8' : 'transparent', borderColor: revisit ? '#E6A817' : 'var(--border)' }}>⚑</button>
        {!r?.result && <span style={{ color: 'var(--danger)' }} title="Not answered">● </span>}
        <span style={{ color: 'var(--text3)', marginRight: 6 }}>{it.kind === 'control' ? it.clause_no : clause}</span>{text}
        {byOther && <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>{r.updated_by} · {new Date(r.updated_at).toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore', hour: 'numeric', minute: '2-digit' })}</div>}</div>
      <div style={{ fontSize: 13, color: 'var(--text3)' }}>
        {last ? <>
          {last.result && <b style={{ color: RESULT_COLOR[last.result] }}>{last.result} </b>}
          <span style={{ whiteSpace: 'pre-wrap' }}>{last.notes}</span>
          {last.notes && !disabled && <div><span style={{ color: 'var(--brand)', cursor: 'pointer' }} onClick={() => { setNotes(last.notes); onSave(it.id, { notes: last.notes }) }}>Copy last note</span></div>}
        </> : null}
      </div>
      <div>
        <select value={r?.result || ''} disabled={disabled} onChange={e => onSave(it.id, { result: e.target.value || null })}
          style={{ width: 'auto', marginBottom: 4, color: RESULT_COLOR[r?.result] || undefined, fontWeight: r?.result ? 600 : 400 }}>
          <option value="">— result —</option>
          {RESULTS.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <textarea rows={notes ? 2 : 1} value={notes} disabled={disabled} placeholder={isFinding(r?.result) ? 'Finding — please describe' : 'Evidence / notes'}
          onChange={e => setNotes(e.target.value)} onBlur={flushNotes}
          style={{ fontSize: 13, borderColor: isFinding(r?.result) && !notes.trim() ? 'var(--danger)' : undefined }} />
        <input value={whom} disabled={disabled} placeholder="Close-out (whom / date)" onChange={e => setWhom(e.target.value)} onBlur={flushWhom}
          style={{ fontSize: 13, marginTop: 4 }} />
      </div>
    </div>
  )
})

function CloseRow({ it, clause, r, who, onSave, reset }) {
  // Same typed-box rule as the checklist rows: the co-auditor's newer note shows while this box isn't being edited.
  const [note, setNote, flushNote] = useRowText(r, 'closed_note', it.id, onSave, false, reset)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 150px', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13, alignItems: 'start' }}>
      <div><b style={{ color: RESULT_COLOR[r?.result] }}>{r?.result}</b> <span style={{ color: 'var(--text3)' }}>{it.kind === 'control' ? it.clause_no : clause}</span> {it.label ? it.label + ' ' : ''}{it.body}
        <div style={{ color: 'var(--text2)', fontSize: 13, whiteSpace: 'pre-wrap' }}>{r?.notes}</div>
        {r?.closeout_whom && <div style={{ color: 'var(--text3)', fontSize: 13 }}>Close-out (whom / date): {r.closeout_whom}</div>}</div>
      <textarea rows={2} placeholder="Close-out evidence seen" value={note} onChange={e => setNote(e.target.value)} onBlur={flushNote} style={{ fontSize: 13 }} />
      <div>
        <label style={{ fontSize: 13 }}>Closed on</label>
        <input type="date" value={r?.closed_at || ''} onChange={e => onSave(it.id, { closed_at: e.target.value || null, closed_by: e.target.value ? who : null })} />
      </div>
    </div>
  )
}

// Pre-send preview — same as bizSAFE: a read-only draft render of the report from the live worksheet (writes
// nothing). The official PDF is produced on send by the report renderer; this is only a look before sending.
function ReportPreview({ preview, logos, onKind, onClose }) {
  const kinds = Object.keys(preview.snaps)
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 60, overflow: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', maxWidth: 900, width: '95%', margin: '3vh auto', borderRadius: 8, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ position: 'sticky', top: 0, background: '#fff', borderBottom: '1px solid var(--border)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderRadius: '8px 8px 0 0', zIndex: 1 }}>
          <strong style={{ fontSize: 14 }}>Report preview <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 13 }}>— draft, not yet sent</span></strong>
          <div style={{ display: 'flex', gap: 6 }}>
            {kinds.length > 1 && kinds.map(k => <button key={k} className={`sm${preview.kind === k ? ' primary' : ''}`} onClick={() => onKind(k)}>{KIND_LABEL[k]}</button>)}
            <button className="sm" onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <IAReportDocument snapshot={preview.snaps[preview.kind]} logos={logos} />
        </div>
      </div>
    </div>
  )
}
